import { Course, CoursePayload, CoursePackage, Module } from "@nicsmith/shared_types";
import { baseApi } from "./api";

export interface CourseAccessResponse {
  has_access: boolean;
  reason: "admin" | "free_course" | "first_module" | "enrolled" | "no_access";
  is_free: boolean;
  packages: CoursePackage[];
  first_module_id: number | null;
}

export interface CourseInviteResult {
  user_id: number;
  success: boolean;
  already_enrolled?: boolean;
  email_sent?: boolean;
  error?: string;
}

export interface CourseInviteResponse {
  total: number;
  success: number;
  newly_enrolled: number;
  emails_sent: number;
  results: CourseInviteResult[];
}

export const courseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all courses
    getAllCourses: builder.query<Course[], { publishedOnly?: boolean } | void>({
      query: (params) => {
        const publishedOnly = params && 'publishedOnly' in params ? params.publishedOnly : true;
        return `/courses?publishedOnly=${publishedOnly}`;
      },
      providesTags: ["Courses"],
    }),

    // Fetch a specific course by ID
    getCourseById: builder.query<Course, string>({
      query: (id) => `/courses/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Courses", id }],
    }),

    // Fetch a specific course by slug
    getCourseBySlug: builder.query<Course, string>({
      query: (slug) => `/courses/slug/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "Courses", id: `slug-${slug}` }],
    }),

    // Create a new course
    createCourse: builder.mutation<Course, CoursePayload>({
      query: (newCourse) => ({
        url: "/courses",
        method: "POST",
        body: newCourse,
      }),
      invalidatesTags: ["Courses"],
    }),

    // Update a course by ID
    updateCourse: builder.mutation<
      Course,
      { id: string; data: Partial<Course> }
    >({
      query: ({ id, data }) => ({
        url: `/courses/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Courses",
        { type: "Courses", id },
      ],
    }),

    // Delete a course by ID
    deleteCourse: builder.mutation<void, string>({
      query: (id) => ({
        url: `/courses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Courses"],
    }),

    // Create a new module within a course
    createModule: builder.mutation<Module, Module>({
      query: (newModule) => ({
        url: "/modules/",
        method: "POST",
        body: newModule,
      }),
      invalidatesTags: ["Module", "Courses"],
    }),

    // Check course access for current user
    getCourseAccess: builder.query<CourseAccessResponse, string>({
      query: (courseId) => `/courses/${courseId}/access`,
      providesTags: (_result, _error, courseId) => [{ type: "Courses", id: `access-${courseId}` }],
    }),

    // Toggle course publication status
    toggleCoursePublication: builder.mutation<Course, { id: string; is_published: boolean }>({
      query: ({ id, is_published }) => ({
        url: `/courses/${id}`,
        method: "PUT",
        body: { is_published },
      }),
      invalidatesTags: ["Courses"],
    }),

    // Reorder courses
    reorderCourses: builder.mutation<void, { ordered_course_ids: number[] }>({
      query: (body) => ({
        url: "/courses/reorder",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Courses"],
    }),

    // Invite users to a course
    inviteUsersToCourse: builder.mutation<
      CourseInviteResponse,
      { courseId: number; user_ids: number[]; send_email: boolean; language?: string }
    >({
      query: ({ courseId, ...body }) => ({
        url: `/courses/${courseId}/invite`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserDetail"],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useGetAllCoursesQuery,
  useGetCourseByIdQuery,
  useGetCourseBySlugQuery,
  useGetCourseAccessQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useCreateModuleMutation,
  useToggleCoursePublicationMutation,
  useReorderCoursesMutation,
  useInviteUsersToCourseMutation,
} = courseApi;
